/**
 * Personalized Relationship Report — verbatim content from
 * "Results Breakdown.pdf". Do not alter wording.
 *
 * Keyed by category (matching CategoryKey in quizConfig.js) and score band.
 * Band thresholds:
 *   0.0 – 4.0  -> "low"
 *   4.1 – 7.0  -> "medium"
 *   7.1 – 10.0 -> "strong"
 */

export const REPORT_CATEGORY_NAMES = {
  foundation: "Communication",
  quick_connect: "Daily Connection",
  sensory_sync: "Intimacy",
  grand_vision: "Shared Vision",
};

export function getBand(score) {
  if (score <= 4) return "low";
  if (score <= 7) return "medium";
  return "strong";
}

export function bandLabel(band) {
  if (band === "low") return "Low";
  if (band === "medium") return "Medium";
  return "Strong";
}

export const REPORT = {
  foundation: {
    low: {
      header: "LOW SCORE (0-4)",
      title: "Connection Is Asking For Attention",
      paragraphs: [
        "Your results suggest that communication and emotional safety may currently be creating challenges in your relationship.",
        "This does not mean the relationship is broken.",
        "It often means that important conversations have been avoided, emotional walls have been built, or both partners no longer feel fully seen and understood.",
        "The good news is that communication is one of the fastest areas to improve when couples intentionally create space for deeper connection.",
        "Use the Foundation Builder cards from Connexus for a month straight and take the quiz again to see if your score has changed.",
      ],
    },
    medium: {
      header: "MEDIUM SCORE (4.1\u20137)",
      title: "There Is More Beneath The Surface",
      paragraphs: [
        "Your relationship has a foundation to build upon, but there may be conversations that are not being fully had, feelings that are not always expressed, or moments where one or both of you hold back what you truly want to say.",
        "Many couples in this range love each other deeply but have fallen into patterns of discussing life instead of discussing themselves.",
        "The opportunity here is creating more emotional openness, vulnerability, and genuine understanding before distance quietly grows.",
        "Start with the Foundation Builder Cards from Connexus for a week and then move into deeper conversations with the Rapid Revelation cards. We especially recommend the sharing withholds and releasing obligations cards for you.",
      ],
    },
    strong: {
      header: "HIGH SCORE (7.1\u201310)",
      title: "Strong Foundation",
      paragraphs: [
        "You and your partner appear to have built a solid foundation of trust, emotional safety, and communication. While no relationship is perfect, you likely feel comfortable sharing what is really going on beneath the surface and addressing challenges together when they arise.",
        "One thing to remember is that strong communication is not something you achieve once and keep forever. The healthiest couples continue creating space for meaningful conversations, curiosity, and emotional honesty as they grow together.",
        "Your opportunity is not necessarily fixing communication. It is continuing to deepen it.",
        "We recommend going even deeper with the Rapid Revelation Cards inside the Connexus Couples Card Deck.",
      ],
    },
  },

  quick_connect: {
    low: {
      header: "LOW SCORE (0-4)",
      title: "Reconnection Needs To Become A Priority",
      paragraphs: [
        "Your answers suggest that daily connection may have slowly taken a back seat to the demands of life.",
        "This often happens gradually and we see that most couples just start talking about logistics. And there is no juice in that.",
        "We see that phones replace presence and responsibilities replace connection.",
        "The relationship still exists, but it no longer feels as alive as it once did.",
        "The good news is that small shifts in daily connection often create surprisingly large improvements in overall relationship satisfaction.",
        "For 16 days straight, use the Quick Connect cards inside the Connexus deck and notice the difference.",
      ],
    },
    medium: {
      header: "MEDIUM SCORE (4.1\u20137)",
      title: "Life May Be Getting In The Way",
      paragraphs: [
        "Your relationship likely still has connection, but it may feel inconsistent.",
        "Some days you feel close and other days life, stress, responsibilities, or routines seem to take over.",
        "This is one of the most common patterns we see in modern relationships.",
        "The opportunity here is not creating more time. It is becoming more intentional with the time you already have.",
        "The best place to start is to get curious with each other. Ask different questions and challenge each other to go deeper every day. The good thing is, it shouldn\u2019t take more than 10 minutes a day when you use the Quick Connect cards in your Connexus deck.",
      ],
    },
    strong: {
      header: "HIGH SCORE (7.1\u201310)",
      title: "The Small Moments Are Working",
      paragraphs: [
        "Your relationship appears to benefit from regular moments of connection throughout daily life. Whether it is checking in, sharing experiences, laughing together, or simply feeling emotionally present with each other, these moments create the glue that keeps relationships strong.",
        "Continue protecting these moments.",
        "The strongest relationships are not built through grand gestures. They are built through thousands of small moments that remind two people they are still choosing each other.",
        "Try out the Quick Connect cards in our deck to change up the conversation with new and refreshing questions.",
      ],
    },
  },

  sensory_sync: {
    low: {
      header: "LOW SCORE (0-4)",
      title: "Your Relationship Is Asking For More Intimacy",
      paragraphs: [
        "Intimacy may currently be one of the biggest opportunities for growth in your relationship.",
        "This does not necessarily mean there is a sexual problem.",
        "More often, it points toward emotional disconnection, unmet needs, lack of affection, reduced desire, or simply not prioritizing intimacy amidst life\u2019s demands.",
        "The beautiful thing about intimacy is that it can be rebuilt.",
        "With intention, curiosity, emotional safety, and connection, many couples experience dramatic shifts in this area.",
        "We recommend you make sure you keep creating a safe environment to play in with your partner. Utilize the Foundation Builder and Rapid Revelations cards inside the Connexus Card Deck to do that.",
        "And then move to the Sensory Sync cards to get more into your body and slowly start the path of creating more intimate moments.",
      ],
    },
    medium: {
      header: "MEDIUM SCORE (4.1\u20137)",
      title: "There Is More Available Here",
      paragraphs: [
        "Your answers suggest that intimacy may still be present, but there is room for deeper connection, desire, affection, or physical closeness.",
        "This is incredibly common. Many couples find that intimacy slowly fades beneath stress, responsibilities, parenting, or emotional disconnection.",
        "The opportunity here is not fixing something that is broken.",
        "It is intentionally creating more space for intimacy to thrive again.",
        "We recommend you make sure you keep creating a safe environment to play in with your partner. Utilize the Foundation Builder and Rapid Revelations cards inside the Connexus Card Deck.",
        "And then move to the Sensory Sync cards to get more into your body to generate the next level of intimacy.",
      ],
    },
    strong: {
      header: "HIGH SCORE (7.1\u201310)",
      title: "Intimacy Is Alive",
      paragraphs: [
        "Your relationship appears to have a healthy level of emotional and physical intimacy.",
        "There is likely a sense of affection, attraction, playfulness, and connection that continues to support your relationship.",
        "That does not mean there is nothing left to explore.",
        "The happiest couples continue intentionally nurturing intimacy rather than assuming it will take care of itself.",
        "The chance is that intimacy fades when it seems everything is going well. We highly recommend using the Sensory Sync cards inside our Connexus Couples Card Deck.",
        "And for couples like you we have a special offering that goes beyond what people normally experience in the bedroom and is centered around extending your orgasm. If you are interested in the experiencing orgasm that goes beyond what you think is imaginable, book a free call with Wouter to explore.",
        "https://calendly.com/woutershahlenis/call-with-wouter",
      ],
    },
  },

  grand_vision: {
    low: {
      header: "LOW SCORE (0-4)",
      title: "It May Be Time To Dream Together Again",
      paragraphs: [
        "Your results suggest that shared vision may currently be one of the biggest growth opportunities in your relationship.",
        "Perhaps conversations about the future feel difficult.",
        "Perhaps you are focused primarily on getting through life rather than building it together.",
        "Or perhaps you simply have not made space for these conversations in a long time.",
        "The strongest relationships are not only built on love. They are built on a shared future that both people feel excited to create.",
        "This is an area where even a few meaningful conversations can completely change the trajectory of a relationship.",
        "Main priority is to create a foundation of trust with the Foundation Builder cards inside the Connexus Couples Card Deck, and from there, you move towards the Grand Vision cards to start the conversation about what it is that you truly want for your shared life.",
        "Remember that the future is built upon a foundation of trust and emotional safety.",
      ],
    },
    medium: {
      header: "MEDIUM SCORE (4.1\u20137)",
      title: "There Is More Future To Create",
      paragraphs: [
        "Your relationship may have some shared direction, but there are likely conversations that still need to happen around dreams, goals, priorities, lifestyle, or the future you want to build together.",
        "Many couples become so focused on managing life that they stop intentionally creating it.",
        "The opportunity here is making space to reconnect around possibility, not just responsibility.",
        "Inside the Connexus deck, you\u2019ll find specific practices in the Grand Vision category that will support you in having the conversations that matter and help you build out your shared Vision.",
      ],
    },
    strong: {
      header: "HIGH SCORE (7.1\u201310)",
      title: "You Are Building Together",
      paragraphs: [
        "Your answers suggest that you and your partner have a healthy sense of shared direction and partnership.",
        "You likely feel aligned on many of the important areas of life and can imagine a future that excites both of you.",
        "This is a powerful strength.",
        "Couples who continue dreaming, planning, and growing together often experience a deeper sense of partnership and purpose within the relationship.",
        "Keep nurturing that vision and take an honest look at how to build out the vision even further. Inside the Connexus deck, you\u2019ll find specific practices in the Grand Vision category that will help you go further with your shared Vision.",
      ],
    },
  },
};
